import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { fetchMyChecklists, createMyChecklist, updateMyChecklist, deleteMyChecklist, fetchChecklistTemplates, shareMyChecklist } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { HERO_ART } from "@/lib/heroes";
import Seo from "@/components/Seo";
import { ListChecks, Plus, Trash2, Check, X, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function MyChecklists() {
  const { pick } = useLang();
  const { user, ready } = useAuth();
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState({});

  const load = () => fetchMyChecklists().then((d) => setLists(d.checklists || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    if (!ready) return;
    if (!user) { setLoading(false); return; }
    fetchChecklistTemplates().then((d) => setTemplates(d.templates || [])).catch(() => {});
    load();
  }, [ready, user]);

  const persist = async (cl) => { try { await updateMyChecklist(cl.id, { title: cl.title, items: cl.items }); } catch (e) { toast.error(e.message); } };
  const patch = (id, fn) => setLists((ls) => ls.map((c) => c.id === id ? fn(c) : c));

  const toggleItem = (cl, item) => { const upd = { ...cl, items: cl.items.map((it) => it.id === item.id ? { ...it, checked: !it.checked } : it) }; patch(cl.id, () => upd); persist(upd); };
  const addItem = (cl) => { const text = (newText[cl.id] || "").trim(); if (!text) return; const upd = { ...cl, items: [...cl.items, { id: `i_${Math.random().toString(36).slice(2, 8)}`, text, checked: false }] }; patch(cl.id, () => upd); persist(upd); setNewText((n) => ({ ...n, [cl.id]: "" })); };
  const removeItem = (cl, item) => { const upd = { ...cl, items: cl.items.filter((it) => it.id !== item.id) }; patch(cl.id, () => upd); persist(upd); };
  const removeList = async (cl) => { if (!window.confirm(`Delete "${cl.title}"?`)) return; try { await deleteMyChecklist(cl.id); setLists((ls) => ls.filter((c) => c.id !== cl.id)); } catch (e) { toast.error(e.message); } };
  const createBlank = async () => { try { const cl = await createMyChecklist({ title: "New Checklist" }); setLists((ls) => [...ls, cl]); } catch (e) { toast.error(e.message); } };
  const createFromTemplate = async (tid) => { try { const cl = await createMyChecklist({ template_id: tid }); setLists((ls) => [...ls, cl]); toast("Checklist added"); } catch (e) { toast.error(e.message); } };
  const share = async (cl) => {
    try {
      const { share_id } = await shareMyChecklist(cl.id);
      const url = `${window.location.origin}/checklist/${share_id}`;
      try { await navigator.clipboard.writeText(url); toast(pick("लिंक कॉपी हो गया", "Share link copied"), { description: url }); }
      catch { toast(pick("साझा लिंक", "Share link"), { description: url }); }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="my-checklists-page">
      <Seo path="/checklists" title="My Pooja Checklists" description="Create and save your own pooja preparation checklists — tick off items as you gather materials for any puja or festival." noindex />
      <PageHero art={HERO_ART.devotion} overline={pick("मेरी सूचियाँ", "My Checklists")} deva="पूजा तैयारी सूची" en={pick("पूजा तैयारी सूची", "Pooja Preparation Checklists")} tagline={pick("किसी भी पूजा या पर्व की तैयारी की सूची बनाएँ, अपनी सामग्री जोड़ें और तैयार होते ही चिह्नित करें — सब आपके खाते में सुरक्षित।", "Build a preparation list for any puja or festival, add your own items, and tick them off as you go — all saved to your account.")} />

      <section className="mx-auto max-w-3xl px-5 py-14 md:px-8">
        {!ready || loading ? (
          <p className="py-16 text-center text-ink-3">…</p>
        ) : !user ? (
          <div className="rounded-xl border border-line bg-surface p-10 text-center">
            <ListChecks className="mx-auto mb-4 h-10 w-10 text-saffron" />
            <p className="text-ink-2">{pick("अपनी पूजा तैयारी सूचियाँ बनाने एवं सहेजने हेतु ", "To create and save your pooja checklists, ")}<Link to="/login" className="text-saffron underline">{pick("साइन इन करें", "sign in")}</Link>.</p>
          </div>
        ) : (
          <>
            <div className="mb-8 flex flex-wrap items-center gap-3">
              <button onClick={createBlank} data-testid="checklist-new-blank" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white"><Plus className="h-4 w-4" /> {pick("नई सूची", "New blank checklist")}</button>
              {templates.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-ink-3">{pick("या टेम्पलेट से:", "or start from a template:")}</span>
                  {templates.map((t) => (
                    <button key={t.id} onClick={() => createFromTemplate(t.id)} data-testid={`checklist-template-${t.id}`} className="rounded-full border border-line px-3 py-1.5 text-xs text-ink-2 hover:border-saffron hover:text-saffron">{t.title}</button>
                  ))}
                </div>
              )}
            </div>

            {lists.length === 0 ? (
              <p className="py-12 text-center text-ink-3">{pick("अभी कोई सूची नहीं — ऊपर से एक बनाएँ।", "No checklists yet — create one above.")}</p>
            ) : (
              <div className="space-y-6" data-testid="checklist-list">
                {lists.map((cl) => {
                  const done = cl.items.filter((i) => i.checked).length;
                  return (
                    <div key={cl.id} className="rounded-xl border border-[var(--gold)]/25 bg-surface p-5" data-testid={`checklist-${cl.id}`}>
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <input value={cl.title} onChange={(e) => patch(cl.id, (c) => ({ ...c, title: e.target.value }))} onBlur={() => persist(cl)} className="flex-1 border-b border-transparent bg-transparent font-serif-latin text-lg text-ink focus:border-line focus:outline-none" data-testid={`checklist-title-${cl.id}`} />
                        <span className="shrink-0 text-xs text-ink-3">{done}/{cl.items.length}</span>
                        <button onClick={() => share(cl)} title={pick("साझा करें", "Share")} className="text-ink-3 hover:text-saffron" data-testid={`checklist-share-${cl.id}`}><Share2 className="h-4 w-4" /></button>
                        <button onClick={() => removeList(cl)} className="text-ink-3 hover:text-red-600" data-testid={`checklist-delete-${cl.id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <ul className="space-y-2">
                        {cl.items.map((it) => (
                          <li key={it.id} className="flex items-center gap-3" data-testid={`checklist-item-${it.id}`}>
                            <button onClick={() => toggleItem(cl, it)} className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${it.checked ? "border-saffron bg-[var(--saffron)] text-white" : "border-line"}`} data-testid={`checklist-toggle-${it.id}`}>{it.checked && <Check className="h-3.5 w-3.5" />}</button>
                            <span className={`flex-1 text-sm ${it.checked ? "text-ink-3 line-through" : "text-ink-2"}`}>{it.text}</span>
                            <button onClick={() => removeItem(cl, it)} className="text-ink-3 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 flex items-center gap-2">
                        <input value={newText[cl.id] || ""} onChange={(e) => setNewText((n) => ({ ...n, [cl.id]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addItem(cl)} placeholder={pick("नई वस्तु जोड़ें…", "Add an item…")} className="flex-1 border border-line bg-parchment px-3 py-1.5 text-sm text-ink focus:border-saffron focus:outline-none" data-testid={`checklist-add-input-${cl.id}`} />
                        <button onClick={() => addItem(cl)} className="inline-flex items-center gap-1 border border-line px-3 py-1.5 text-sm text-ink-2 hover:border-saffron hover:text-saffron" data-testid={`checklist-add-btn-${cl.id}`}><Plus className="h-4 w-4" /> {pick("जोड़ें", "Add")}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
